import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/auth-provider";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { useProducts } from "@/hooks/use-products";
import { Product } from "@shared/operations-schema";
import { useLocation } from "wouter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Layout } from "@/components/layout";
import { 
  Package, 
  Settings, 
  Filter,
  Search,
  Plus,
  X,
  MoreHorizontal,
  AlertCircle,
  ChevronDown,
  PackageOpen,
  Pencil,
  Trash2,
  Loader2,
  Store,
  DollarSign,
  CircleDashed
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";

// Define the product schema
const ProductSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  description: z.string().optional().or(z.literal("")),
  type: z.enum(["product", "service"]),
  sku: z.string().optional().or(z.literal("")),
  price: z.coerce.number().min(0, "Price must be a positive number"),
  cost: z.coerce.number().min(0, "Cost must be a positive number").optional(),
  taxRate: z.coerce.number().min(0, "Tax rate must be a positive number").default(0),
  isActive: z.boolean().default(true),
  imageUrl: z.string().optional().or(z.literal("")),
  unit: z.string().default("each"),
});

type ProductFormValues = z.infer<typeof ProductSchema>;

const defaultValues: ProductFormValues = {
  name: "",
  description: "",
  type: "product",
  sku: "",
  price: 0,
  cost: 0,
  taxRate: 0,
  isActive: true,
  imageUrl: "",
  unit: "each",
};

// Main Products/Services Page Component
export default function ProductsPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBusiness } = useCurrentBusiness();
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState<"all" | "products" | "services">("all");
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "detail">("list");

  // Form setup
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(ProductSchema),
    defaultValues,
  });

  // Use products hook
  const { 
    products = [], 
    isLoading, 
    error,
    createProductMutation,
    updateProductMutation,
    deleteProductMutation
  } = useProducts();

  // Filtered products based on search term and active tab
  const filteredProducts = products?.filter((product) => {
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (activeTab === "all") return matchesSearch;
    return matchesSearch && product.type === activeTab.slice(0, -1); // "products" -> "product", "services" -> "service"
  });

  // Create wrapper functions to add UI-specific functionality
  const handleCreateProduct = (data: ProductFormValues) => {
    createProductMutation.mutate(data, {
      onSuccess: () => {
        setIsAddProductOpen(false);
        form.reset(defaultValues);
      }
    });
  };

  const handleUpdateProduct = (id: number, data: ProductFormValues) => {
    updateProductMutation.mutate({ id, data }, {
      onSuccess: () => {
        setIsEditProductOpen(false);
        if (viewMode === "detail") {
          setViewMode("list");
          setSelectedProduct(null);
        }
      }
    });
  };

  const handleDeleteProduct = (id: number) => {
    deleteProductMutation.mutate(id, {
      onSuccess: () => {
        if (viewMode === "detail") {
          setViewMode("list");
          setSelectedProduct(null);
        }
      },
      onError: (error: Error) => {
        toast({
          title: "Failed to delete product",
          description: error.message,
          variant: "destructive",
        });
      }
    });
  };

  // Form handling
  const onSubmit = (data: ProductFormValues) => {
    if (isEditProductOpen && selectedProduct) {
      handleUpdateProduct(selectedProduct.id, data);
    } else {
      handleCreateProduct(data);
    }
  };

  // Track whether we're initializing the form after opening edit dialog
  const [initializedEdit, setInitializedEdit] = useState(false);
  
  // When selectedProduct or isEditProductOpen changes, update form
  useEffect(() => {
    if (selectedProduct && isEditProductOpen && !initializedEdit) {
      console.log("Setting form values from useEffect:", selectedProduct);
      
      form.reset({
        name: selectedProduct.name,
        description: selectedProduct.description || "",
        type: selectedProduct.type as "product" | "service", 
        sku: selectedProduct.sku || "",
        price: Number(selectedProduct.price),
        cost: selectedProduct.cost ? Number(selectedProduct.cost) : 0,
        taxRate: Number(selectedProduct.taxRate),
        isActive: selectedProduct.isActive ?? true,
        imageUrl: selectedProduct.imageUrl || "",
        unit: selectedProduct.unit || "each",
      });
      
      setInitializedEdit(true);
    } else if (!isEditProductOpen) {
      // Reset the initialization flag when dialog closes
      setInitializedEdit(false);
    }
  }, [selectedProduct, isEditProductOpen, form, initializedEdit]);
  
  // Edit handler that navigates to dedicated edit page
  const handleEditProduct = (product: Product) => {
    console.log("Edit product clicked:", product);
    // Save product to localStorage for quick access in edit page
    localStorage.setItem('editProduct', JSON.stringify(product));
    // Navigate to edit page
    navigate(`/sales/products/edit/${product.id}`);
  };

  // Handle product detail view
  const handleViewProduct = (product: Product) => {
    setSelectedProduct(product);
    setViewMode("detail");
  };

  // Handle back to list view
  const handleBackToList = () => {
    setViewMode("list");
    setSelectedProduct(null);
  };

  // Format currency
  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  // Reset form on dialog close
  useEffect(() => {
    if (!isAddProductOpen && !isEditProductOpen) {
      form.reset(defaultValues);
    }
  }, [isAddProductOpen, isEditProductOpen, form]);

  if (!currentBusiness) {
    return (
      <Layout>
        <div className="container mx-auto py-10">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-medium mb-2">No Business Selected</h2>
              <p className="text-muted-foreground mb-4">Please select or create a business to manage products.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-10">
          <div className="flex justify-center items-center h-[60vh]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="container mx-auto py-10">
          <div className="rounded-md bg-destructive/10 p-8 text-center">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-medium mb-2">Error loading products</h2>
            <p className="text-muted-foreground">{(error as Error).message}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Product Detail View
  if (viewMode === "detail" && selectedProduct) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex items-center mb-6">
            <Button variant="ghost" onClick={handleBackToList} className="mr-4">
              <X className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            <h1 className="text-xl font-semibold">Product Details</h1>
          </div>

        <div className="space-y-6">
          {/* Product Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="bg-muted rounded-md flex items-center justify-center h-16 w-16">
                {selectedProduct.type === "product" ? (
                  <Package className="h-8 w-8 text-muted-foreground" />
                ) : (
                  <Settings className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-2xl font-bold">{selectedProduct.name}</h2>
                  <Badge variant={selectedProduct.isActive !== false ? "default" : "secondary"}>
                    {selectedProduct.isActive !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Badge variant="outline">
                    {selectedProduct.type === "product" ? "Product" : "Service"}
                  </Badge>
                  {selectedProduct.sku && (
                    <span className="text-sm">SKU: {selectedProduct.sku}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => handleEditProduct(selectedProduct)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the
                      {selectedProduct.type === "product" ? " product" : " service"} and remove it
                      from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteProduct(selectedProduct.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteProductMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Delete"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          <Separator />

          {/* Product Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">Description</Label>
                  <p className="text-sm mt-1">{selectedProduct.description || "No description available"}</p>
                </div>
                <div>
                  <Label className="text-xs">Unit</Label>
                  <p className="text-sm mt-1">{selectedProduct.unit || "each"}</p>
                </div>
                {selectedProduct.imageUrl && (
                  <div>
                    <Label className="text-xs">Image</Label>
                    <div className="mt-1 h-32 w-auto rounded-md overflow-hidden">
                      <img 
                        src={selectedProduct.imageUrl} 
                        alt={selectedProduct.name} 
                        className="object-cover h-full w-full"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pricing Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Selling Price</Label>
                  <span className="font-semibold text-xl">{formatCurrency(selectedProduct.price)}</span>
                </div>
                {selectedProduct.cost && (
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">Cost</Label>
                    <span>{formatCurrency(selectedProduct.cost)}</span>
                  </div>
                )}
                {selectedProduct.cost && (
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">Profit Margin</Label>
                    <span>
                      {(() => {
                        const price = Number(selectedProduct.price);
                        const cost = Number(selectedProduct.cost);
                        if (price === 0) return "0%";
                        const margin = ((price - cost) / price) * 100;
                        return `${margin.toFixed(2)}%`;
                      })()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <Label className="text-xs">Tax Rate</Label>
                  <span>{Number(selectedProduct.taxRate).toFixed(2)}%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Usage Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col items-center justify-center h-32">
                  <CircleDashed className="h-10 w-10 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center text-sm">
                    {selectedProduct.type === "product"
                      ? "Product usage statistics will appear here"
                      : "Service usage statistics will appear here"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Documents and Transactions Tabs */}
          <Tabs defaultValue="invoices">
            <TabsList>
              <TabsTrigger value="invoices">Invoices</TabsTrigger>
              <TabsTrigger value="estimates">Estimates</TabsTrigger>
            </TabsList>
            <TabsContent value="invoices" className="py-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No invoices yet</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="estimates" className="py-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No estimates yet</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Edit Product/Service Dialog */}
        <Dialog open={isEditProductOpen} onOpenChange={(open) => {
            if (!open) {
              setIsEditProductOpen(false);
              // Reset form on dialog close
              setTimeout(() => form.reset(defaultValues), 100);
            }
          }}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                Edit {selectedProduct && selectedProduct.type === "product" ? "Product" : "Service"}
              </DialogTitle>
              <DialogDescription>
                Update {selectedProduct ? selectedProduct.type : ""} information.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="product" id="product" />
                            <Label htmlFor="product">Product</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="service" id="service" />
                            <Label htmlFor="service">Service</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Product name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input type="number" step="0.01" min="0" placeholder="0.00" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cost (Optional)</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input type="number" step="0.01" min="0" placeholder="0.00" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="SKU" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit</FormLabel>
                        <FormControl>
                          <Input placeholder="each, hour, day, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="taxRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax Rate (%)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.01" 
                            min="0" 
                            placeholder="0"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-md border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Active</FormLabel>
                          <FormDescription>
                            Mark as active to display in product list
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Image URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com/image.jpg" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Product description" 
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditProductOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={updateProductMutation.isPending}
                  >
                    {updateProductMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
    );
  }

  // List view
  return (
    <Layout>
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <Store className="h-6 w-6" />
            <h1 className="text-xl font-semibold">Products & Services</h1>
          </div>
          <Button onClick={() => setIsAddProductOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>

      <Tabs defaultValue="all" onValueChange={(value) => setActiveTab(value as "all" | "products" | "services")}>
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
            </TabsList>

            <div className="relative w-full sm:w-[300px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
              {searchTerm && (
                <X
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer"
                  onClick={() => setSearchTerm("")}
                />
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
          </div>
        </div>

        {filteredProducts && filteredProducts.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">
                      <Button
                        variant="ghost"
                        className="p-0 h-auto font-medium text-left hover:bg-transparent hover:underline"
                        onClick={() => handleViewProduct(product)}
                      >
                        {product.name}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {product.type === "product" ? (
                          <Package className="h-3 w-3 mr-1" />
                        ) : (
                          <Settings className="h-3 w-3 mr-1" />
                        )}
                        {product.type.charAt(0).toUpperCase() + product.type.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>{product.sku || "-"}</TableCell>
                    <TableCell>{formatCurrency(product.price)}</TableCell>
                    <TableCell>
                      <Badge variant={product.isActive !== false ? "default" : "secondary"}>
                        {product.isActive !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleViewProduct(product)}>
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditProduct(product)}>
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setSelectedProduct(product);
                              // Will trigger alert dialog 
                            }}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed p-12">
            {activeTab === "all" ? (
              <PackageOpen className="h-10 w-10 text-muted-foreground mb-4" />
            ) : activeTab === "products" ? (
              <Package className="h-10 w-10 text-muted-foreground mb-4" />
            ) : (
              <Settings className="h-10 w-10 text-muted-foreground mb-4" />
            )}
            <h3 className="text-lg font-medium mb-2">
              {searchTerm
                ? "No items match your search"
                : activeTab === "all"
                ? "No products or services found"
                : activeTab === "products"
                ? "No products found"
                : "No services found"}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {searchTerm
                ? `Try a different search term or clear the filter`
                : activeTab === "all"
                ? "Get started by adding your first product or service"
                : activeTab === "products"
                ? "Add your first product to your inventory"
                : "Add your first service to your catalog"}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsAddProductOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {activeTab === "all"
                  ? "Add Item"
                  : activeTab === "products"
                  ? "Add Product"
                  : "Add Service"}
              </Button>
            )}
          </div>
        )}

        {/* Add Product Dialog */}
        <Dialog open={isAddProductOpen} onOpenChange={setIsAddProductOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Add Product or Service</DialogTitle>
              <DialogDescription>
                Add a new product or service to your business.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="product" id="product" />
                            <Label htmlFor="product">Product</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="service" id="service" />
                            <Label htmlFor="service">Service</Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Product name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input type="number" step="0.01" min="0" placeholder="0.00" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cost (Optional)</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input type="number" step="0.01" min="0" placeholder="0.00" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU (Optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="SKU" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit</FormLabel>
                        <FormControl>
                          <Input placeholder="each, hour, day, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="taxRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax Rate (%)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.01" 
                            min="0" 
                            placeholder="0"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-md border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Active</FormLabel>
                          <FormDescription>
                            Mark as active to display in product list
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Image URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://example.com/image.jpg" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Product description" 
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddProductOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={createProductMutation.isPending}
                  >
                    {createProductMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Item
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Product Dialog */}
        {selectedProduct && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              {/* Button is within the dropdown menu */}
              <span className="hidden"></span>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the
                  {selectedProduct.type === "product" ? " product" : " service"} and remove it
                  from our servers.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => handleDeleteProduct(selectedProduct.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deleteProductMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </Tabs>
    </div>
    </Layout>
  );
}
